import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Eye, EyeOff, UserPlus, ArrowLeft } from 'lucide-react';
import { Button, Card, Input } from '../components/ui';
import { authService } from '../services/authService';
import { useRegistrationStore } from '../stores/registrationStore';
import { ProgressStepper } from '../components/onboarding/ProgressStepper';
import { EmailConfirmationModal } from '../components/onboarding/EmailConfirmationModal';

interface PasswordValidation {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasDigit: boolean;
  hasSpecial: boolean;
}

const RegistrationPage: React.FC = () => {
  const { setRegistrationData } = useRegistrationStore();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedTierId, setSelectedTierId] = useState<number | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    organizationName: '',
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasDigit: false,
    hasSpecial: false,
  });

  // Extract tier and billing cycle from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tierIdParam = params.get('selectedTierId');
    const billingParam = params.get('billingCycle');

    if (tierIdParam) {
      setSelectedTierId(parseInt(tierIdParam));
    }
    if (billingParam === 'monthly' || billingParam === 'annual') {
      setBillingCycle(billingParam);
    }
  }, []);

  // Update password validation in real-time
  useEffect(() => {
    const password = formData.password;
    setPasswordValidation({
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasDigit: /\d/.test(password),
      hasSpecial: /[@$!%*?&]/.test(password),
    });
  }, [formData.password]);

  const getTierName = (tierId: number | null) => {
    switch (tierId) {
      case 1:
        return 'Starter';
      case 2:
        return 'Growth';
      case 3:
        return 'Professional';
      default:
        return 'Selected';
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.organizationName.trim()) {
      errors.organizationName = 'Organization name is required';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (
      !passwordValidation.hasUppercase ||
      !passwordValidation.hasLowercase ||
      !passwordValidation.hasDigit ||
      !passwordValidation.hasSpecial
    ) {
      errors.password = 'Password must meet all requirements';
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors({ ...validationErrors, [field]: '' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Show confirmation modal before submitting
    setShowConfirmationModal(true);
  };

  const handleConfirmRegistration = async () => {
    setLoading(true);

    try {
      const response = await authService.register({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        organizationName: formData.organizationName,
        subscriptionTierId: selectedTierId || 1,
      });

      if (response.success) {
        // Store registration data for use in subsequent steps
        setRegistrationData({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          selectedTierId: selectedTierId || 1,
          billingCycle: billingCycle,
          emailVerified: false,
        });

        // Close modal and redirect to email verification waiting page
        setShowConfirmationModal(false);
        window.location.href = '/onboarding/verify-email';
      } else {
        setValidationErrors({
          submit: response.error?.message || 'Registration failed. Please try again.',
        });
        setShowConfirmationModal(false);
      }

    } catch (error) {
      setValidationErrors({
        submit: error instanceof Error ? error.message : 'An unexpected error occurred',
      });
      setShowConfirmationModal(false);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToPricing = () => {
    window.location.href = '/pricing';
  };

  const steps = [
    { label: 'Account', description: 'Create account' },
    { label: 'Verify Email', description: 'Confirm email' },
    { label: 'Payment', description: 'Enter payment' },
    { label: 'Complete', description: 'Start trial' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="container mx-auto px-4 py-6 sm:py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <button
            onClick={handleBackToPricing}
            className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Pricing
          </button>
          {selectedTierId && (
            <div className="text-xs sm:text-sm text-gray-600">
              Selected Plan: <span className="font-semibold text-avi-primary">{getTierName(selectedTierId)}</span>
              {' '}({billingCycle === 'annual' ? 'Annual' : 'Monthly'})
            </div>
          )}
        </div>

        {/* Progress Stepper */}
        <ProgressStepper steps={steps} currentStep={0} />

        {/* Registration Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-xl mx-auto"
        >
          <Card className="p-5 sm:p-6">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 bg-avi-primary bg-opacity-10 rounded-full flex items-center justify-center">
                <UserPlus className="w-6 h-6 text-avi-primary" />
              </div>
            </div>

            <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-900 mb-1">
              Create Your Account
            </h1>
            <p className="text-center text-sm text-gray-600 mb-5">
              Get started with your 14-day free trial
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="firstName" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <Input
                    id="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className={validationErrors.firstName ? 'border-red-500' : ''}
                    placeholder="John"
                  />
                  {validationErrors.firstName && (
                    <p className="text-red-500 text-xs mt-1">{validationErrors.firstName}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <Input
                    id="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className={validationErrors.lastName ? 'border-red-500' : ''}
                    placeholder="Doe"
                  />
                  {validationErrors.lastName && (
                    <p className="text-red-500 text-xs mt-1">{validationErrors.lastName}</p>
                  )}
                </div>
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={validationErrors.email ? 'border-red-500' : ''}
                  placeholder="john@company.com"
                />
                {validationErrors.email && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors.email}</p>
                )}
              </div>

              {/* Organization Name */}
              <div>
                <label htmlFor="organizationName" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Organization Name *
                </label>
                <Input
                  id="organizationName"
                  type="text"
                  value={formData.organizationName}
                  onChange={(e) => handleInputChange('organizationName', e.target.value)}
                  className={validationErrors.organizationName ? 'border-red-500' : ''}
                  placeholder="Company Inc."
                />
                {validationErrors.organizationName && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors.organizationName}</p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Password *
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={validationErrors.password ? 'border-red-500' : ''}
                    placeholder="Create a strong password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                {/* Password Requirements */}
                {formData.password && (
                  <div className="mt-2 space-y-1">
                    <div className="flex items-center gap-1.5">
                      <div
                        className={`w-1.5 h-1.5 rounded-full ${
                          passwordValidation.minLength ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      />
                      <span
                        className={`text-xs ${
                          passwordValidation.minLength ? 'text-green-600' : 'text-gray-500'
                        }`}
                      >
                        At least 8 characters
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div
                        className={`w-1.5 h-1.5 rounded-full ${
                          passwordValidation.hasUppercase ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      />
                      <span
                        className={`text-xs ${
                          passwordValidation.hasUppercase ? 'text-green-600' : 'text-gray-500'
                        }`}
                      >
                        One uppercase letter
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div
                        className={`w-1.5 h-1.5 rounded-full ${
                          passwordValidation.hasLowercase ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      />
                      <span
                        className={`text-xs ${
                          passwordValidation.hasLowercase ? 'text-green-600' : 'text-gray-500'
                        }`}
                      >
                        One lowercase letter
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div
                        className={`w-1.5 h-1.5 rounded-full ${
                          passwordValidation.hasDigit ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      />
                      <span
                        className={`text-xs ${
                          passwordValidation.hasDigit ? 'text-green-600' : 'text-gray-500'
                        }`}
                      >
                        One number
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div
                        className={`w-1.5 h-1.5 rounded-full ${
                          passwordValidation.hasSpecial ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      />
                      <span
                        className={`text-xs ${
                          passwordValidation.hasSpecial ? 'text-green-600' : 'text-gray-500'
                        }`}
                      >
                        One special character (@$!%*?&)
                      </span>
                    </div>
                  </div>
                )}

                {validationErrors.password && !formData.password && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors.password}</p>
                )}
              </div>

              {/* Confirm Password Field */}
              <div>
                <label htmlFor="confirmPassword" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Confirm Password *
                </label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    className={validationErrors.confirmPassword ? 'border-red-500' : ''}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {validationErrors.confirmPassword && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors.confirmPassword}</p>
                )}
              </div>

              {/* Submit Error */}
              {validationErrors.submit && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-xs sm:text-sm">{validationErrors.submit}</p>
                </div>
              )}

              {/* Submit Button */}
              <Button type="submit" variant="primary" size="md" className="w-full text-sm" disabled={loading}>
                Create Account
              </Button>

              {/* Terms */}
              <p className="text-[10px] sm:text-xs text-center text-gray-500">
                By creating an account, you agree to our{' '}
                <a href="/terms" className="text-avi-primary hover:underline">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="/privacy" className="text-avi-primary hover:underline">
                  Privacy Policy
                </a>
              </p>
            </form>
          </Card>
        </motion.div>

        {/* Email Confirmation Modal */}
        <EmailConfirmationModal
          isOpen={showConfirmationModal}
          email={formData.email}
          onConfirm={handleConfirmRegistration}
          onEdit={() => setShowConfirmationModal(false)}
          onClose={() => setShowConfirmationModal(false)}
          isLoading={loading}
        />
      </div>
    </div>
  );
};

export default RegistrationPage;
